export type PaymentModeScope = 'SUBSCRIPTION'

import { apiURI } from './constants'
import { putRequest } from '../core/api-client'
import {
  BulkPGConfigPayload,
  PaymentGatewayPriority,
  PaymentMethodResponse,
  UpdateGatewayPayload
} from 'src/lib/types/paymentSettings'
import { PAYMENT_METHOD, paymentGatewaysConfig, paymentMethods } from 'src/assets/SettingsConfig'
import { PAYMENT_GATEWAY } from 'src/assets/SettingsConfig'
import { IPaymentGateways } from '../types/settings'
import { BulkBgUpdateDialogState } from '../types/paymentSettings'
import { UpiIntentItems } from '@shopflo/ui'

export const INITIAL_BULK_PG_UPDATE_STATE: BulkBgUpdateDialogState = {
  isOpen: false
}

export const updateGateway = async (
  arg: UpdateGatewayPayload | BulkPGConfigPayload,
  merchant_id: string,
  modeType?: PaymentModeScope
) => {
  const query = modeType ? `?purchase_type=${modeType}` : ''
  const response = await putRequest(
    `/md/merchant/${merchant_id}/payment-modes${query}`,
    apiURI.FLO_KRATOS_CHECKOUT,
    arg
  )
  if (response?.error) {
    throw response.error
  }
  return response
}

export const reversePriority = (priority: 0 | 1): 0 | 1 => {
  switch (priority) {
    case 0:
      return 1
    case 1:
      return 0
  }
}

export const getOptimisticData = (
  data: PaymentMethodResponse[],
  payload: {
    method: PAYMENT_METHOD
    priority: 0 | 1
    swapGateways?: boolean
  } & ({ isRemoval: true } | { isRemoval: false; gateway: PAYMENT_GATEWAY })
) => {
  return data.map(conf => {
    if (conf.mode !== payload.method) {
      return conf
    }

    if (payload.swapGateways) {
      // Matches createPayload's swap logic - flips priority for every gateway on this method,
      // instead of only touching the single priority/gateway pair the caller clicked.
      return {
        ...conf,
        gateways: conf.gateways.map(gateway => ({ ...gateway, priority: reversePriority(gateway.priority) }))
      }
    }

    const gateways = conf.gateways.filter(gateway => gateway.priority !== payload.priority)

    if ('gateway' in payload) {
      gateways.push({
        pg_name: payload.gateway,
        priority: payload.priority
      })
    }

    return { ...conf, gateways }
  })
}

export const getPriorityValue = (priority: PaymentGatewayPriority) => {
  switch (priority) {
    case PaymentGatewayPriority.PRIMARY:
      return 0
    case PaymentGatewayPriority.SECONDARY:
      return 1
    default:
      return -1
  }
}

export const getNormalizedAllowedGateways = () => {
  return paymentGatewaysConfig.reduce((acc, method: IPaymentGateways) => {
    return {
      ...acc,
      [method.type]: method
    }
  }, {})
}

export const getNormalizedPaymentMethods = () => {
  return paymentMethods.reduce((acc, method: IPaymentGateways) => {
    return {
      ...acc,
      [method.type]: method
    }
  }, {})
}

export const createPayload = ({
  bulkUpdate,
  gateway,
  optimisticData,
  method,
  isRemoval
}: {
  bulkUpdate: boolean
  gateway: string
  optimisticData: PaymentMethodResponse[]
  method: PAYMENT_METHOD
  isRemoval: boolean
}): UpdateGatewayPayload | BulkPGConfigPayload => {
  const bulkConfigPGs = [
    'UPI',
    'PAY_LATER',
    'UPI_COLLECT',
    'NETBANKING',
    'EMI',
    'WALLET',
    'CARD',
    'UPI_INTENT',
    'SPLIT_COD'
  ]

  if (bulkUpdate) {
    return {
      modes: bulkConfigPGs.map(mode => ({
        name: mode,
        gateways: [{ name: getPaymentGateWayMode(gateway), priority: 0 }]
      }))
    } as BulkPGConfigPayload
  }

  const currentConfiguration = optimisticData.find(item => item.mode === method)

  if (!currentConfiguration?.gateways) {
    throw new Error('Invalid configuration for selected gateway')
  }

  return {
    mode: {
      name: method,
      // optimisticData already reflects the final desired state (including swaps), so this is a direct transcription - no priority transform needed here.
      gateways: currentConfiguration.gateways.map(gateway => {
        return {
          name: gateway.pg_name,
          priority: gateway.priority
        }
      })
    }
  } as UpdateGatewayPayload
}

export const PAYMENT_GATEWAY_MAP: { [key: string]: PAYMENT_GATEWAY } = {
  Razorpay: PAYMENT_GATEWAY.RP,
  Cashfree: PAYMENT_GATEWAY.CF,
  Snapmint: PAYMENT_GATEWAY.SNPM,
  Velocity: PAYMENT_GATEWAY.VELOCITY,
  PayU: PAYMENT_GATEWAY.PAYU,
  Paytm: PAYMENT_GATEWAY.PAYTM,
  CCAvenue: PAYMENT_GATEWAY.CCAVENUE,
  PhonePe: PAYMENT_GATEWAY.PHONEPE,
  Easebuzz: PAYMENT_GATEWAY.EASEBUZZ
}

export const getPaymentGateWayMode = (gateway: string): PAYMENT_GATEWAY | '' => {
  return PAYMENT_GATEWAY_MAP[gateway] || ''
}

export const getGatewayMode = (gateway: string, bulkUpdate: boolean) => {
  if (bulkUpdate) {
    return getPaymentGateWayMode(gateway)
  }
  return gateway
}

export const getActiveApps = (apps: UpiIntentItems[]) => {
  return apps.filter(item => item.is_active)
}
